import { useState, useEffect, ChangeEvent } from 'react';
import toast from 'react-hot-toast';
import { Header } from '../components/Header';
import { CreditCostNotice } from '../components/credits/CreditCostNotice';
import { useCreditConfirm } from '../components/credits/useCreditConfirm';
import { ContactLabelSelector } from '../components/ContactLabelSelector';
import { ContactCategorySelector } from '../components/ContactCategorySelector';
import { useCredits } from '../hooks/useCredits';


const BRAZILIAN_STATES = [
  { uf: 'AC', name: 'Acre' }, { uf: 'AL', name: 'Alagoas' }, { uf: 'AP', name: 'Amapá' },
  { uf: 'AM', name: 'Amazonas' }, { uf: 'BA', name: 'Bahia' }, { uf: 'CE', name: 'Ceará' },
  { uf: 'DF', name: 'Distrito Federal' }, { uf: 'ES', name: 'Espírito Santo' },
  { uf: 'GO', name: 'Goiás' }, { uf: 'MA', name: 'Maranhão' }, { uf: 'MT', name: 'Mato Grosso' },
  { uf: 'MS', name: 'Mato Grosso do Sul' }, { uf: 'MG', name: 'Minas Gerais' },
  { uf: 'PA', name: 'Pará' }, { uf: 'PB', name: 'Paraíba' }, { uf: 'PR', name: 'Paraná' },
  { uf: 'PE', name: 'Pernambuco' }, { uf: 'PI', name: 'Piauí' }, { uf: 'RJ', name: 'Rio de Janeiro' },
  { uf: 'RN', name: 'Rio Grande do Norte' }, { uf: 'RS', name: 'Rio Grande do Sul' },
  { uf: 'RO', name: 'Rondônia' }, { uf: 'RR', name: 'Roraima' }, { uf: 'SC', name: 'Santa Catarina' },
  { uf: 'SP', name: 'São Paulo' }, { uf: 'SE', name: 'Sergipe' }, { uf: 'TO', name: 'Tocantins' }
];

const SITUACOES_CADASTRAIS = [
  { value: 'ATIVA', label: 'Ativa' },
  { value: 'BAIXADA', label: 'Baixada' },
  { value: 'INAPTA', label: 'Inapta' },
  { value: 'SUSPENSA', label: 'Suspensa' },
  { value: 'NULA', label: 'Nula' }
];

type ContactCategory = {
  id: string;
  nome: string;
};

type AILeadAnalysis = {
  empresa: string;
  telefone: string;
  email: string;
  endereco: string;
  description: string;
};

type Company = {
  cnpj: string;
  cnpjRaw: string;
  razaoSocial: string;
  nomeFantasia: string;
  situacao: string;
  dataAbertura: string;
  capitalSocial: number;
  porte: string;
  naturezaJuridica: string;
  atividadePrincipal: string;
  cnae: string;
  endereco: {
    logradouro?: string;
    numero?: string;
    complemento?: string;
    bairro?: string;
    municipio?: string;
    uf?: string;
    cep?: string;
  } | null;
  enderecoCompleto: string;
  telefones: string[];
  telefone: string | null;
  email: string | null;
  socios: string[];
  loadingPhone?: boolean;
  aiLoading?: boolean;
  aiAnalysis?: AILeadAnalysis | null;
};

export function CNPJSearchPage() {
  const { refetch: refetchCredits } = useCredits();

  // Config
  const [savingConfig, setSavingConfig] = useState(false);

  // IA lead analysis
  const [leadIdeal, setLeadIdeal] = useState('');
  const [leadAnulado, setLeadAnulado] = useState('');
  const [analyzingAI, setAnalyzingAI] = useState(false);
  const { request: requestCnpjConfirm, modal: cnpjConfirmModal } = useCreditConfirm();

  // Search filters
  const [termo, setTermo] = useState('');
  const [uf, setUf] = useState('');
  const [municipio, setMunicipio] = useState('');
  const [bairro, setBairro] = useState('');
  const [cep, setCep] = useState('');
  const [ddd, setDdd] = useState('');
  const [situacaoCadastral, setSituacaoCadastral] = useState('ATIVA');
  const [atividadePrincipal, setAtividadePrincipal] = useState('');
  const [dataAberturaInicio, setDataAberturaInicio] = useState('');
  const [dataAberturaFim, setDataAberturaFim] = useState('');
  const [capitalSocialMinimo, setCapitalSocialMinimo] = useState('');
  const [capitalSocialMaximo, setCapitalSocialMaximo] = useState('');
  const [somenteMatriz, setSomenteMatriz] = useState(false);
  const [somenteFilial, setSomenteFilial] = useState(false);
  const [comEmail, setComEmail] = useState(false);
  const [comTelefone, setComTelefone] = useState(true);
  const [somenteCelular, setSomenteCelular] = useState(false);
  const [somenteFixo, setSomenteFixo] = useState(false);

  // Pagination
  const [limite, setLimite] = useState(20);
  const [pagina, setPagina] = useState(1);
  const [total, setTotal] = useState(0);

  // Results
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState<Company[]>([]);
  const [contactCategories, setContactCategories] = useState<ContactCategory[]>([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [selectedCategoryId, setSelectedCategoryId] = useState<string>('');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [savingContactId, setSavingContactId] = useState<string>('');
  const [savingAll, setSavingAll] = useState(false);

  // Seleção de empresas para buscar telefone
  const [selectedCompanies, setSelectedCompanies] = useState<Set<string>>(new Set());
  const [fetchingPhones, setFetchingPhones] = useState(false);

  // Advanced filters toggle
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);

  const authenticatedFetch = async (url: string, options: RequestInit = {}) => {
    const token = localStorage.getItem('auth_token');
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...options.headers
    };
    if (token) {
      (headers as Record<string, string>).Authorization = `Bearer ${token}`;
    }
    return fetch(url, {
      ...options,
      headers
    });
  };

  const normalizeAIResponseItem = (item: any): AILeadAnalysis | null => {
    const raw = Array.isArray(item?.output) ? item.output[0] : item;
    if (!raw || typeof raw !== 'object') return null;
    return {
      empresa: typeof raw.empresa === 'string' ? raw.empresa : '',
      telefone: typeof raw.telefone === 'string' ? raw.telefone : '',
      email: typeof raw.email === 'string' ? raw.email : '',
      endereco:
        typeof (raw as any)['endereço'] === 'string'
          ? (raw as any)['endereço']
          : typeof (raw as any).endereco === 'string'
            ? (raw as any).endereco
            : '',
      description: typeof raw.description === 'string' ? raw.description : ''
    };
  };

  const applyAIResultToCompany = (company: Company, analysis: AILeadAnalysis | null): Company => {
    if (!analysis) {
      return { ...company, aiLoading: false, aiAnalysis: null };
    }

    const telefoneNormalized = analysis.telefone && analysis.telefone.toUpperCase() !== 'ANULADO' ? analysis.telefone : null;
    const emailNormalized = analysis.email && analysis.email.toUpperCase() !== 'ANULADO' ? analysis.email : null;
    const enderecoNormalized = analysis.endereco && analysis.endereco.toUpperCase() !== 'ANULADO' ? analysis.endereco : null;

    return {
      ...company,
      aiLoading: false,
      aiAnalysis: analysis,
      telefone: telefoneNormalized ?? company.telefone,
      email: emailNormalized ?? company.email,
      enderecoCompleto: enderecoNormalized ?? company.enderecoCompleto
    };
  };

  useEffect(() => {
    loadLeadCriteria();
    loadContactCategories();
  }, []);

  const loadContactCategories = async () => {
    try {
      setLoadingCategories(true);
      const response = await authenticatedFetch('/api/categorias/all');
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      const data = await response.json();
      setContactCategories(data);
    } catch (error) {
      console.error('[CNPJ] Erro ao carregar categorias:', error);
      toast.error('Erro ao carregar categorias de contatos');
    } finally {
      setLoadingCategories(false);
    }
  };

  const loadLeadCriteria = async () => {
    try {
      const response = await authenticatedFetch('/api/cnpj/lead-criteria');
      if (response.ok) {
        const data = await response.json();
        setLeadIdeal(data.leadIdeal || '');
        setLeadAnulado(data.leadAnulado || '');
      }
    } catch (error) {
      console.error('[CNPJ] Erro ao carregar critérios de lead:', error);
    }
  };

  const handleSaveConfig = async () => {
    try {
      setSavingConfig(true);

      const leadResponse = await authenticatedFetch('/api/cnpj/lead-criteria', {
        method: 'POST',
        body: JSON.stringify({ leadIdeal, leadAnulado })
      });

      if (!leadResponse.ok) throw new Error('Erro ao salvar critérios de lead');

      toast.success('Configuração salva com sucesso!');
    } catch (error) {
      console.error('[CNPJ] Erro ao salvar configuração:', error);
      toast.error('Erro ao salvar configuração');
    } finally {
      setSavingConfig(false);
    }
  };

  const handleSearch = async (newPage = 1) => {
    try {
      setSearching(true);
      setPagina(newPage);

      const filters: any = {
        limite,
        pagina: newPage
      };

      // Termo de busca
      if (termo.trim()) {
        filters.termo = [termo.trim()];
      }

      // Localização
      if (uf) filters.uf = [uf];
      if (municipio.trim()) filters.municipio = [municipio.trim()];
      if (bairro.trim()) filters.bairro = [bairro.trim()];
      if (cep.trim()) filters.cep = [cep.replace(/\D/g, '')];
      if (ddd.trim()) filters.ddd = [ddd.trim()];

      // Situação cadastral
      if (situacaoCadastral) filters.situacao_cadastral = [situacaoCadastral];

      // Atividade principal (CNAE)
      if (atividadePrincipal.trim()) {
        filters.atividade_principal = [atividadePrincipal.replace(/\D/g, '')];
      }

      // Data de abertura
      if (dataAberturaInicio) filters.data_abertura_inicio = dataAberturaInicio;
      if (dataAberturaFim) filters.data_abertura_fim = dataAberturaFim;

      // Capital social
      if (capitalSocialMinimo) filters.capital_social_minimo = parseFloat(capitalSocialMinimo);
      if (capitalSocialMaximo) filters.capital_social_maximo = parseFloat(capitalSocialMaximo);

      // Mais filtros
      if (somenteMatriz) filters.somente_matriz = true;
      if (somenteFilial) filters.somente_filial = true;
      if (comEmail) filters.com_email = true;
      if (comTelefone) filters.com_telefone = true;
      if (somenteCelular) filters.somente_celular = true;
      if (somenteFixo) filters.somente_fixo = true;

      const response = await authenticatedFetch('/api/cnpj/search', {
        method: 'POST',
        body: JSON.stringify(filters)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Erro ao buscar empresas');
      }

      const data = await response.json();
      setResults(data.companies || []);
      setTotal(data.total || 0);
      refetchCredits();
      toast.success(`${data.companies?.length || 0} empresas encontradas (total: ${data.total || 0})`);
    } catch (error) {
      console.error('[CNPJ] Erro na busca:', error);
      toast.error(error instanceof Error ? error.message : 'Erro ao buscar empresas');
    } finally {
      setSearching(false);
    }
  };

  const handleSaveCompanies = async (companiesToSave: Company[], scope: 'single' | 'all') => {
    if (!companiesToSave || companiesToSave.length === 0) {
      toast.error('Nenhuma empresa válida para salvar');
      return;
    }

    try {
      if (scope === 'all') {
        setSavingAll(true);
      } else {
        setSavingContactId(companiesToSave[0]?.cnpjRaw || '');
      }

      const response = await authenticatedFetch('/api/cnpj/save', {
        method: 'POST',
        body: JSON.stringify({
          companies: companiesToSave.map((company) => ({
            nomeFantasia: company.nomeFantasia,
            razaoSocial: company.razaoSocial,
            telefone: company.telefone,
            enderecoCompleto: company.enderecoCompleto || company.aiAnalysis?.endereco || null,
            cnpj: company.cnpj,
            email: company.email,
            observacoes: company.aiAnalysis?.description || null
          })),
          categoryId: selectedCategoryId || null,
          tags: selectedTags
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Erro ao salvar contatos');
      }

      const result = await response.json();
      toast.success(
        `Contatos salvos! ${result.imported} novos, ${result.updated} atualizados, ${result.skipped} ignorados`
      );
    } catch (error) {
      console.error('[CNPJ] Erro ao salvar contatos:', error);
      toast.error(
        error instanceof Error ? error.message : 'Erro ao salvar contatos'
      );
    } finally {
      setSavingAll(false);
      setSavingContactId('');
    }
  };

  const handleSaveSingle = (company: Company) => {
    if (!company.telefone) {
      toast.error('Empresa sem telefone não pode ser salva');
      return;
    }
    handleSaveCompanies([company], 'single');
  };

  // Buscar telefone de um CNPJ individual
  const handleFetchPhone = async (cnpjRaw: string) => {
    try {
      // Marcar como carregando
      setResults(prev => prev.map(c => 
        c.cnpjRaw === cnpjRaw ? { ...c, loadingPhone: true } : c
      ));

      const response = await authenticatedFetch(`/api/cnpj/consulta/${cnpjRaw}`);
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Erro ao buscar telefone');
      }

      const data = await response.json();
      
      // Atualizar o resultado com os dados completos
      setResults(prev => prev.map(c => 
        c.cnpjRaw === cnpjRaw ? { 
          ...c, 
          telefone: data.telefone,
          telefones: data.telefones || [],
          email: data.email || c.email,
          enderecoCompleto: data.enderecoCompleto || c.enderecoCompleto,
          endereco: data.endereco || c.endereco,
          loadingPhone: false 
        } : c
      ));

      if (data.telefone) {
        toast.success(`Telefone encontrado: ${data.telefone}`);
      } else {
        toast.error('Nenhum telefone encontrado para este CNPJ');
      }
      refetchCredits();
    } catch (error) {
      console.error('[CNPJ] Erro ao buscar telefone:', error);
      toast.error(error instanceof Error ? error.message : 'Erro ao buscar telefone');
      setResults(prev => prev.map(c => 
        c.cnpjRaw === cnpjRaw ? { ...c, loadingPhone: false } : c
      ));
    }
  };

  // Buscar telefone de múltiplos CNPJs selecionados
  const handleFetchSelectedPhones = async () => {
    if (selectedCompanies.size === 0) {
      toast.error('Selecione ao menos uma empresa');
      return;
    }

    setFetchingPhones(true);
    const cnpjs = Array.from(selectedCompanies);
    let successCount = 0;
    let errorCount = 0;

    for (const cnpjRaw of cnpjs) {
      try {
        // Marcar como carregando
        setResults(prev => prev.map(c => 
          c.cnpjRaw === cnpjRaw ? { ...c, loadingPhone: true } : c
        ));

        const response = await authenticatedFetch(`/api/cnpj/consulta/${cnpjRaw}`);
        
        if (!response.ok) {
          throw new Error('Erro na consulta');
        }

        const data = await response.json();
        
        // Atualizar o resultado
        setResults(prev => prev.map(c => 
          c.cnpjRaw === cnpjRaw ? { 
            ...c, 
            telefone: data.telefone,
            telefones: data.telefones || [],
            email: data.email || c.email,
            enderecoCompleto: data.enderecoCompleto || c.enderecoCompleto,
            endereco: data.endereco || c.endereco,
            loadingPhone: false 
          } : c
        ));

        if (data.telefone) successCount++;
        else errorCount++;

        // Pequeno delay entre requisições para não sobrecarregar a API
        await new Promise(resolve => setTimeout(resolve, 500));
      } catch (error) {
        console.error(`[CNPJ] Erro ao buscar telefone do CNPJ ${cnpjRaw}:`, error);
        errorCount++;
        setResults(prev => prev.map(c => 
          c.cnpjRaw === cnpjRaw ? { ...c, loadingPhone: false } : c
        ));
      }
    }

    setFetchingPhones(false);
    setSelectedCompanies(new Set());
    refetchCredits();
    toast.success(`Busca concluída: ${successCount} telefones encontrados, ${errorCount} sem telefone`);
  };

  const handleAnalyzeAI = async (companiesToAnalyze: Company[]) => {
    if (companiesToAnalyze.length === 0) {
      toast.error('Nenhuma empresa para analisar');
      return;
    }
    if (!leadIdeal.trim()) {
      toast.error('Preencha o campo "lead ideal" (critério que orienta a IA) antes de analisar');
      return;
    }

    try {
      setAnalyzingAI(true);
      const cnpjSet = new Set(companiesToAnalyze.map((c) => c.cnpjRaw));
      setResults((prev) => prev.map((c) => (cnpjSet.has(c.cnpjRaw) ? { ...c, aiLoading: true } : c)));

      const response = await authenticatedFetch('/api/cnpj/ia-analise-lead', {
        method: 'POST',
        body: JSON.stringify({
          companies: companiesToAnalyze.map((c) => ({
            nomeFantasia: c.nomeFantasia,
            razaoSocial: c.razaoSocial,
            cnpj: c.cnpj
          })),
          leadIdeal,
          leadAnulado
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Erro ao analisar lead com IA');
      }

      const data = await response.json();
      const items = Array.isArray(data) ? data : [];

      setResults((prev) => {
        const updated = [...prev];
        companiesToAnalyze.forEach((company, idx) => {
          const analysis = normalizeAIResponseItem(items[idx]);
          const pos = updated.findIndex((c) => c.cnpjRaw === company.cnpjRaw);
          if (pos >= 0) {
            updated[pos] = applyAIResultToCompany(updated[pos], analysis);
          }
        });
        return updated;
      });

      refetchCredits();
      toast.success('Análise com IA concluída');
    } catch (error) {
      console.error('[CNPJ] Erro ao analisar lead com IA:', error);
      toast.error(error instanceof Error ? error.message : 'Erro ao analisar lead com IA');
      const cnpjSet = new Set(companiesToAnalyze.map((c) => c.cnpjRaw));
      setResults((prev) => prev.map((c) => (cnpjSet.has(c.cnpjRaw) ? { ...c, aiLoading: false } : c)));
    } finally {
      setAnalyzingAI(false);
    }
  };

  // Toggle seleção de empresa
  const toggleSelectCompany = (cnpjRaw: string) => {
    setSelectedCompanies(prev => {
      const newSet = new Set(prev);
      if (newSet.has(cnpjRaw)) {
        newSet.delete(cnpjRaw);
      } else {
        newSet.add(cnpjRaw);
      }
      return newSet;
    });
  };

  // Selecionar/deselecionar todos
  const toggleSelectAll = () => {
    if (selectedCompanies.size === results.length) {
      setSelectedCompanies(new Set());
    } else {
      setSelectedCompanies(new Set(results.map(c => c.cnpjRaw)));
    }
  };

  const handleSaveAll = () => {
    const companiesWithPhone = results.filter((company) => !!company.telefone);
    if (companiesWithPhone.length === 0) {
      toast.error('Nenhuma empresa com telefone para salvar');
      return;
    }
    handleSaveCompanies(companiesWithPhone, 'all');
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const totalPages = Math.ceil(total / limite);

  return (
    <div className="min-h-screen bg-gray-50">
      {cnpjConfirmModal}
      <Header title="Busca de CNPJs" />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Configuração de Lead IA */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div>
            <h3 className="text-sm font-semibold text-gray-900 mb-3">Ajude a IA a identificar seu Lead Ideal.</h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Lead ideal</label>
                <textarea
                  value={leadIdeal}
                  onChange={(e: ChangeEvent<HTMLTextAreaElement>) => setLeadIdeal(e.target.value)}
                  placeholder="Descreva o lead ideal para o seu negócio"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  rows={2}
                />
                <p className="text-xs text-gray-500 mt-1">
                  A IA usa este critério para reconhecer empresas com maior potencial de compra.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Lead anulado</label>
                <textarea
                  value={leadAnulado}
                  onChange={(e: ChangeEvent<HTMLTextAreaElement>) => setLeadAnulado(e.target.value)}
                  placeholder="Descreva quais empresas devem ser descartadas (ANULADO)"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  rows={2}
                />
                <p className="text-xs text-gray-500 mt-1">
                  A IA usa este critério para descartar empresas que não fazem sentido para você.
                </p>
              </div>
            </div>

            <div className="mt-4 flex justify-end">
              <button
                onClick={handleSaveConfig}
                disabled={savingConfig}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                {savingConfig ? 'Salvando...' : 'Salvar'}
              </button>
            </div>
          </div>
        </div>

        {/* Formulário de Busca */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">🔍 Buscar Empresas</h2>
          
          {/* Filtros Básicos */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Termo de Busca
              </label>
              <input
                type="text"
                value={termo}
                onChange={(e) => setTermo(e.target.value)}
                placeholder="Ex: Corte laser, Restaurante, Padaria..."
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
              <p className="text-xs text-gray-500 mt-1">
                Busca por radical na razão social e nome fantasia.
              </p>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Estado (UF)</label>
              <select
                value={uf}
                onChange={(e) => setUf(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Todos os estados</option>
                {BRAZILIAN_STATES.map(s => (
                  <option key={s.uf} value={s.uf}>{s.uf} - {s.name}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Município</label>
              <input
                type="text"
                value={municipio}
                onChange={(e) => setMunicipio(e.target.value)}
                placeholder="Ex: São Paulo"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Bairro</label>
              <input
                type="text"
                value={bairro}
                onChange={(e) => setBairro(e.target.value)}
                placeholder="Ex: Centro"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Situação Cadastral</label>
              <select
                value={situacaoCadastral}
                onChange={(e) => setSituacaoCadastral(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Todas</option>
                {SITUACOES_CADASTRAIS.map(s => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">DDD</label>
              <input
                type="text"
                value={ddd}
                onChange={(e) => setDdd(e.target.value)}
                placeholder="Ex: 11, 21, 31..."
                maxLength={2}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* Toggle Filtros Avançados */}
          <button
            type="button"
            onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
            className="text-blue-600 hover:text-blue-800 text-sm font-medium mb-4 flex items-center gap-1"
          >
            {showAdvancedFilters ? '▼' : '▶'} Filtros Avançados
          </button>

          {/* Filtros Avançados */}
          {showAdvancedFilters && (
            <div className="border-t pt-4 mb-4">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">CEP</label>
                  <input
                    type="text"
                    value={cep}
                    onChange={(e) => setCep(e.target.value)}
                    placeholder="Ex: 01310-100"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">CNAE (Atividade Principal)</label>
                  <input
                    type="text"
                    value={atividadePrincipal}
                    onChange={(e) => setAtividadePrincipal(e.target.value)}
                    placeholder="Ex: 5611201"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Data Abertura (Início)</label>
                  <input
                    type="date"
                    value={dataAberturaInicio}
                    onChange={(e) => setDataAberturaInicio(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Data Abertura (Fim)</label>
                  <input
                    type="date"
                    value={dataAberturaFim}
                    onChange={(e) => setDataAberturaFim(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Capital Social Mínimo (R$)</label>
                  <input
                    type="number"
                    value={capitalSocialMinimo}
                    onChange={(e) => setCapitalSocialMinimo(e.target.value)}
                    placeholder="Ex: 10000"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Capital Social Máximo (R$)</label>
                  <input
                    type="number"
                    value={capitalSocialMaximo}
                    onChange={(e) => setCapitalSocialMaximo(e.target.value)}
                    placeholder="Ex: 1000000"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              {/* Checkboxes */}
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={somenteMatriz}
                    onChange={(e) => {
                      setSomenteMatriz(e.target.checked);
                      if (e.target.checked) setSomenteFilial(false);
                    }}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  Somente Matriz
                </label>

                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={somenteFilial}
                    onChange={(e) => {
                      setSomenteFilial(e.target.checked);
                      if (e.target.checked) setSomenteMatriz(false);
                    }}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  Somente Filial
                </label>

                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={comEmail}
                    onChange={(e) => setComEmail(e.target.checked)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  Com E-mail
                </label>

                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={comTelefone}
                    onChange={(e) => setComTelefone(e.target.checked)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  Com Telefone
                </label>

                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={somenteCelular}
                    onChange={(e) => {
                      setSomenteCelular(e.target.checked);
                      if (e.target.checked) setSomenteFixo(false);
                    }}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  Somente Celular
                </label>

                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={somenteFixo}
                    onChange={(e) => {
                      setSomenteFixo(e.target.checked);
                      if (e.target.checked) setSomenteCelular(false);
                    }}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  Somente Fixo
                </label>
              </div>
            </div>
          )}

          {/* Limite de resultados */}
          <div className="flex items-center gap-4 mb-4">
            <label className="text-sm font-medium text-gray-700">Resultados por página:</label>
            <select
              value={limite}
              onChange={(e) => setLimite(Number(e.target.value))}
              className="px-3 py-1 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>

          <div className="mb-2">
            <CreditCostNotice items={[{ featureKey: 'cnpj_query', qty: 1 }]} perUnit note="A qualificação com IA é cobrada à parte, por empresa analisada." />
          </div>
          <div className="flex items-center justify-between">
            <button
              onClick={() => requestCnpjConfirm({
                title: 'Buscar empresas (Casa dos Dados)',
                items: [{ featureKey: 'cnpj_query', qty: 1 }],
                note: 'Cada busca consome este valor, independente da quantidade de resultados.',
                confirmLabel: 'Buscar',
                onConfirm: () => handleSearch(1),
              })}
              disabled={searching}
              className="flex-1 px-6 py-3 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
            >
              {searching ? 'Buscando...' : '🔍 Buscar Empresas'}
            </button>
          </div>
        </div>

        {/* Resultados */}
        {results.length > 0 && (
          <div className="bg-white rounded-lg shadow p-4 sm:p-6">
            <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-center mb-4">
              <h2 className="text-lg font-semibold text-gray-900">
                Resultados ({results.length} de {total})
              </h2>
              
              {/* Paginação */}
              {totalPages > 1 && (
                <div className="flex flex-wrap items-center gap-2">
                  <button
                    onClick={() => handleSearch(pagina - 1)}
                    disabled={pagina <= 1 || searching}
                    className="px-3 py-1 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                  >
                    ← Anterior
                  </button>
                  <span className="text-sm text-gray-600">
                    Página {pagina} de {totalPages}
                  </span>
                  <button
                    onClick={() => handleSearch(pagina + 1)}
                    disabled={pagina >= totalPages || searching}
                    className="px-3 py-1 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                  >
                    Próxima →
                  </button>
                </div>
              )}
            </div>

            {/* Ações em lote */}
            <div className="mb-4 border rounded-lg p-4 bg-gray-50">
              <div className="flex flex-col gap-4">
                {/* Linha de seleção e busca de telefone */}
                <div className="flex flex-col md:flex-row md:items-center gap-4">
                  <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                    <input
                      type="checkbox"
                      checked={selectedCompanies.size === results.length && results.length > 0}
                      onChange={toggleSelectAll}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    Selecionar todos ({selectedCompanies.size}/{results.length})
                  </label>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <button
                      onClick={() => requestCnpjConfirm({
                        title: 'Qualificar leads com IA',
                        items: [{ featureKey: 'cnpj_ai_lead', qty: selectedCompanies.size, label: 'empresa(s)' }],
                        confirmLabel: 'Analisar com IA',
                        onConfirm: () => handleAnalyzeAI(results.filter((c) => selectedCompanies.has(c.cnpjRaw))),
                      })}
                      disabled={analyzingAI || selectedCompanies.size === 0}
                      className="px-4 py-2 bg-indigo-600 text-white rounded-lg font-semibold hover:bg-indigo-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                    >
                      {analyzingAI ? 'Analisando...' : `🤖 Análise com IA (${selectedCompanies.size})`}
                    </button>
                    <button
                      onClick={handleFetchSelectedPhones}
                      disabled={fetchingPhones || selectedCompanies.size === 0}
                      className="hidden px-4 py-2 bg-purple-600 text-white rounded-lg font-semibold hover:bg-purple-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                    >
                      {fetchingPhones ? 'Buscando telefones...' : `📞 Buscar telefone de selecionados (${selectedCompanies.size})`}
                    </button>
                  </div>
                </div>
                {selectedCompanies.size > 0 && (
                  <div className="mt-3">
                    <CreditCostNotice items={[{ featureKey: 'cnpj_ai_lead', qty: selectedCompanies.size, label: 'empresas' }]} />
                  </div>
                )}

                {/* Linha de categoria e salvar */}
                <div className="flex flex-col md:flex-row md:items-end gap-4">
                  <div className="flex-1">
                    <ContactCategorySelector categories={contactCategories} value={selectedCategoryId} onChange={setSelectedCategoryId} label="Categoria de contatos (opcional)" />
                    {loadingCategories && (
                      <p className="text-xs text-gray-500 mt-1">Carregando categorias...</p>
                    )}
                  </div>
                  <div className="flex-1"><ContactLabelSelector value={selectedTags} onChange={setSelectedTags} /></div>
                  <div className="md:w-56">
                    <button
                      onClick={handleSaveAll}
                      disabled={savingAll || results.every((company) => !company.telefone)}
                      className="w-full px-4 py-2 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                    >
                      {savingAll ? 'Salvando tudo...' : 'Salvar todos os contatos'}
                    </button>
                    <p className="text-xs text-gray-500 mt-2">
                      Apenas empresas com telefone serão salvas.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Lista de empresas */}
            <div className="space-y-4">
              {results.map((company, index) => (
                <div key={index} className={`border rounded-lg p-4 hover:bg-gray-50 ${selectedCompanies.has(company.cnpjRaw) ? 'border-blue-500 bg-blue-50' : 'border-gray-200'}`}>
                  <div className="flex flex-col gap-3 md:flex-row md:justify-between md:items-start">
                    <div className="flex items-start gap-3 flex-1 min-w-0">
                      {/* Checkbox de seleção */}
                      <input
                        type="checkbox"
                        checked={selectedCompanies.has(company.cnpjRaw)}
                        onChange={() => toggleSelectCompany(company.cnpjRaw)}
                        className="mt-1 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold text-gray-900">{company.nomeFantasia}</h3>
                          <span className={`px-2 py-0.5 text-xs rounded-full ${
                            company.situacao === 'ATIVA' 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {company.situacao}
                          </span>
                        </div>
                        {company.nomeFantasia !== company.razaoSocial && (
                          <p className="text-sm text-gray-500 mb-1">{company.razaoSocial}</p>
                        )}
                        <p className="text-sm text-gray-600 mb-2">
                          <span className="font-medium">CNPJ:</span> {company.cnpj}
                        </p>
                        
                        {/* Endereço */}
                        {company.endereco && (
                          <p className="text-sm text-gray-600 mb-2">
                            <span className="font-medium">📍 Endereço:</span>{' '}
                            {company.endereco.logradouro && `${company.endereco.logradouro}`}
                            {company.endereco.numero && `, ${company.endereco.numero}`}
                            {company.endereco.complemento && ` - ${company.endereco.complemento}`}
                            {company.endereco.bairro && ` - ${company.endereco.bairro}`}
                            {company.endereco.municipio && `, ${company.endereco.municipio}`}
                            {company.endereco.uf && ` - ${company.endereco.uf}`}
                            {company.endereco.cep && ` (CEP: ${company.endereco.cep})`}
                          </p>
                        )}
                        {!company.endereco && company.enderecoCompleto && company.enderecoCompleto !== 'Endereço não disponível' && (
                          <p className="text-sm text-gray-600 mb-2">
                            <span className="font-medium">📍 Endereço:</span> {company.enderecoCompleto}
                          </p>
                        )}
                        
                        <div className="mt-2 flex flex-wrap gap-4 text-sm">
                          {company.telefone ? (
                            <span className="text-blue-600 font-medium">📞 {company.telefone}</span>
                          ) : (
                            <span className="text-gray-400">📞 Sem telefone</span>
                          )}
                          {company.email && (
                            <span className="text-purple-600">✉️ {company.email}</span>
                          )}
                          {company.capitalSocial > 0 && (
                            <span className="text-green-600">💰 {formatCurrency(company.capitalSocial)}</span>
                          )}
                          {company.porte !== 'N/A' && (
                            <span className="text-gray-600">🏢 {company.porte}</span>
                          )}
                        </div>

                        {company.atividadePrincipal !== 'N/A' && (
                          <p className="text-xs text-gray-500 mt-2">
                            <span className="font-medium">Atividade:</span> {company.atividadePrincipal}
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="flex flex-col gap-2 md:ml-4 shrink-0">
                      <button
                        onClick={() => requestCnpjConfirm({
                          title: 'Qualificar lead com IA',
                          items: [{ featureKey: 'cnpj_ai_lead', qty: 1, label: 'empresa' }],
                          confirmLabel: 'Analisar com IA',
                          onConfirm: () => handleAnalyzeAI([company]),
                        })}
                        disabled={company.aiLoading || analyzingAI}
                        className="px-4 py-2 text-sm font-medium bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                      >
                        {company.aiLoading ? 'Analisando...' : '🤖 Análise com IA'}
                      </button>

                      {/* Botão buscar telefone */}
                      <button
                        onClick={() => handleFetchPhone(company.cnpjRaw)}
                        disabled={company.loadingPhone || fetchingPhones}
                        className="hidden px-4 py-2 text-sm font-medium bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                      >
                        {company.loadingPhone ? 'Buscando...' : '📞 Buscar telefone'}
                      </button>
                      
                      {/* Botão salvar contato */}
                      <button
                        onClick={() => handleSaveSingle(company)}
                        disabled={
                          !company.telefone ||
                          savingAll ||
                          savingContactId === company.cnpjRaw ||
                          company.telefone.toUpperCase() === 'ANULADO' ||
                          company.aiAnalysis?.empresa?.toUpperCase() === 'ANULADO'
                        }
                        className="px-4 py-2 text-sm font-medium bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                      >
                        {!company.telefone
                          ? 'Sem telefone'
                          : savingContactId === company.cnpjRaw
                          ? 'Salvando...'
                          : 'Salvar contato'}
                      </button>
                    </div>
                  </div>

                  {company.aiAnalysis && (
                    <div className="mt-4 border-t pt-3">
                      <div className="flex flex-col gap-2">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-semibold text-gray-700">Resultado IA:</span>
                          <span className={`px-2 py-0.5 text-xs rounded-full ${
                            company.aiAnalysis.empresa?.toUpperCase() === 'ANULADO'
                              ? 'bg-red-100 text-red-800'
                              : 'bg-green-100 text-green-800'
                          }`}>
                            {company.aiAnalysis.empresa || 'N/A'}
                          </span>
                        </div>
                        <div className="flex flex-wrap gap-4 text-sm">
                          <span className={`font-medium ${
                            company.aiAnalysis.telefone?.toUpperCase() === 'ANULADO'
                              ? 'text-red-600'
                              : 'text-blue-600'
                          }`}>
                            📞 {company.aiAnalysis.telefone || 'N/A'}
                          </span>
                          <span className={`font-medium ${
                            company.aiAnalysis.email?.toUpperCase() === 'ANULADO'
                              ? 'text-red-600'
                              : 'text-purple-600'
                          }`}>
                            ✉️ {company.aiAnalysis.email || 'N/A'}
                          </span>
                          <span className={`font-medium ${
                            company.aiAnalysis.endereco?.toUpperCase() === 'ANULADO'
                              ? 'text-red-600'
                              : 'text-gray-700'
                          }`}>
                            📍 {company.aiAnalysis.endereco || 'N/A'}
                          </span>
                        </div>
                        {company.aiAnalysis.description && (
                          <p className="text-sm text-gray-700 whitespace-pre-line">{company.aiAnalysis.description}</p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Paginação inferior */}
            {totalPages > 1 && (
              <div className="flex justify-center items-center gap-2 mt-6 pt-4 border-t">
                <button
                  onClick={() => handleSearch(pagina - 1)}
                  disabled={pagina <= 1 || searching}
                  className="px-4 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  ← Anterior
                </button>
                <span className="text-sm text-gray-600 mx-4">
                  Página {pagina} de {totalPages}
                </span>
                <button
                  onClick={() => handleSearch(pagina + 1)}
                  disabled={pagina >= totalPages || searching}
                  className="px-4 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  Próxima →
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
